import React, { useState } from 'react';
import { Check, HelpCircle, Shield, Clock, Hammer, Users } from 'lucide-react';
import Button from '../components/ui/Button';

const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  
  const plans = [
    {
      name: 'Basic',
      description: 'Perfect for occasional tool borrowers',
      price: billingPeriod === 'monthly' ? 0 : 0,
      features: [
        'Up to 3 tool rentals per month',
        'Basic insurance coverage',
        'Community forum access',
        'Email support',
      ],
      button: {
        text: 'Get Started',
        variant: 'outline' as const,
      },
      popular: false,
    },
    {
      name: 'Pro',
      description: 'For active tool sharers and borrowers',
      price: billingPeriod === 'monthly' ? 9.99 : 99.99,
      features: [
        'Unlimited tool rentals',
        'Premium insurance coverage',
        'Priority support',
        'Verified member badge',
        'Early access to new tools',
        'Exclusive member discounts',
      ],
      button: {
        text: 'Start Free Trial',
        variant: 'primary' as const,
      },
      popular: true,
    },
    {
      name: 'Business',
      description: 'Ideal for professional tool lenders',
      price: billingPeriod === 'monthly' ? 29.99 : 299.99,
      features: [
        'Everything in Pro, plus:',
        'Multiple tool listings',
        'Business profile',
        'Analytics dashboard',
        'API access',
        'Dedicated account manager',
      ],
      button: {
        text: 'Contact Sales',
        variant: 'outline' as const,
      },
      popular: false,
    },
  ];

  const features = [
    {
      icon: <Shield className="h-6 w-6" />,
      name: 'Insurance Coverage',
      description: 'All rentals include basic insurance coverage for peace of mind.',
    },
    {
      icon: <Clock className="h-6 w-6" />,
      name: 'Flexible Rentals',
      description: 'Rent tools for as short as one day or as long as you need.',
    },
    {
      icon: <Hammer className="h-6 w-6" />,
      name: 'Quality Tools',
      description: 'Access to a wide variety of well-maintained tools.',
    },
    {
      icon: <Users className="h-6 w-6" />,
      name: 'Community',
      description: 'Join a trusted network of tool sharers in your area.',
    },
  ];

  const faqs = [
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, including Visa, Mastercard, and American Express. We also support PayPal for secure payments.',
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time. If you cancel, you\'ll continue to have access to your plan until the end of your current billing period.',
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes, our Pro plan comes with a 14-day free trial. You can explore all the features and cancel anytime during the trial period.',
    },
    {
      question: 'What is included in the insurance coverage?',
      answer: 'Basic insurance covers accidental damage up to $1,000. Premium insurance (included in Pro and Business plans) provides coverage up to $5,000 and includes theft protection.',
    },
  ];

  return (
    <div className="bg-gray-50 py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-600">
            Choose the perfect plan for your tool sharing needs
          </p>
          
          {/* Billing Toggle */}
          <div className="mt-8 flex justify-center items-center space-x-4">
            <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Monthly billing
            </span>
            <button
              onClick={() => setBillingPeriod(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                billingPeriod === 'yearly' ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span className="sr-only">Toggle billing period</span>
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  billingPeriod === 'yearly' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm ${billingPeriod === 'yearly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Yearly billing
              <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-lg shadow-sm border ${
                plan.popular ? 'border-green-600' : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <span className="bg-green-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
                <p className="mt-2 text-gray-600">{plan.description}</p>
                
                <div className="mt-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                    <span className="ml-2 text-gray-500">
                      /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </div>
                </div>
                
                <ul className="mt-6 space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mr-2" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-8">
                  <Button
                    variant={plan.button.variant}
                    fullWidth
                    className={plan.popular ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {plan.button.text}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Everything you need to share tools
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.name} className="text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{feature.name}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-8">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start">
                  <HelpCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 mb-8">
            Our team is here to help you find the perfect plan for your needs.
          </p>
          <div className="flex justify-center space-x-4">
            <Button variant="outline">Contact Sales</Button>
            <Button variant="primary">Get Started</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;